import React, { useEffect, useState } from "react";
import { Table, Spin, message, DatePicker, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import "./Time.css";
import { API_BASE_URL } from "shared/api/config";

dayjs.extend(isBetween);

const { RangePicker } = DatePicker;

const hourlyRate = 7000;

interface TimeLog {
  _id: string;
  employee?: string;
  description?: string;
  date: string; // "YYYY-MM-DD"
  clockIn: string;
  clockOut: string;
  createdAt: string;
}

const TimeLogList: React.FC = () => {
  const [logs, setLogs] = useState<TimeLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);

  const computeHours = (ci: string, co: string) => {
    const start = dayjs(ci, "HH:mm");
    const end = dayjs(co, "HH:mm");
    const diff = end.diff(start, "minute");
    return +(diff / 60).toFixed(2);
  };

  
  const filteredLogs = logs.filter((log) => {
    const matchDateRange =
      dateRange[0] && dateRange[1]
        ? dayjs(log.date).isBetween(dateRange[0], dateRange[1], "day", "[]")
        : true;

    return matchDateRange;
  });

  const dataSource = filteredLogs.map((log) => ({
    key: log._id,
    ...log,
    hours: computeHours(log.clockIn, log.clockOut),
    salary: Math.round(computeHours(log.clockIn, log.clockOut) * hourlyRate),
  }));

  const columns: ColumnsType<any> = [
    { title: "Employee", dataIndex: "employee", key: "employee" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Clock In", dataIndex: "clockIn", key: "clockIn" },
    { title: "Clock Out", dataIndex: "clockOut", key: "clockOut" },
    { title: "Hours", dataIndex: "hours", key: "hours" },
    {
      title: "Salary",
      dataIndex: "salary",
      key: "salary",
      render: (value: number) => value.toLocaleString() + "₮",
    },
    { title: "Created At", dataIndex: "createdAt", key: "createdAt" },
  ];

  const fetchTimeLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/time/getall`);
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching time logs:", error);
      message.error("Failed to load time logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeLogs();
  }, []);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Time Logs</h2>

      <Space className="mb-4" direction="horizontal">
        Огноо :{" "}
        <RangePicker
          allowClear
          format="YYYY-MM-DD"
          onChange={(dates) => setDateRange(dates ?? [null, null])}
        />
      </Space>

      {loading ? (
        <Spin />
      ) : (
        <Table
          className="custom-table"
          dataSource={dataSource}
          columns={columns}
          rowKey="key"
          pagination={{ pageSize: 10 }}
          footer={() => (
            <div style={{ textAlign: "right" }}>
              <strong>Total Entries:</strong> {dataSource.length}
              <br />
              <strong>Total Hours:</strong>{" "}
              {dataSource.reduce((sum, r) => sum + r.hours, 0).toFixed(2)} hours
              <br />
              <strong>Total Salary:</strong>{" "}
              {dataSource
                .reduce((sum, r) => sum + r.salary, 0)
                .toLocaleString()}
              ₮
            </div>
          )}
        />
      )}
    </div>
  );
};

export default TimeLogList;
